import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { Sparkles, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ProposalDiffCard, type Proposal } from "@/components/domain/proposal-diff-card"

// Proposal kinds that mutate the SkillVersion (and therefore require a
// draft). Scenario kinds edit TestScenario rows directly — those aren't
// versioned, so a scenario-only apply succeeds against a live skill
// with no draft.
const SKILL_TOUCHING_KINDS = new Set([
  "propose_workflow_update",
  "propose_field_update",
])

function needsDraftFor(proposals: Proposal[]): boolean {
  return proposals.some((p) => p.accepted && SKILL_TOUCHING_KINDS.has(p.kind))
}

type Props = {
  skillId: number
  proposals: Proposal[]
  setProposals: (next: Proposal[] | ((cur: Proposal[]) => Proposal[])) => void
  /** Current persisted skill snapshot — feeds the before-state of diff cards. */
  skillSnapshot: Record<string, unknown> | null
  /** Persisted scenarios for scenario_update / scenario_remove diffs. */
  scenarios: Array<Record<string, unknown>>
  /** Skill must be a draft for apply to succeed (server enforces too). */
  isDraft: boolean
  /** Replace the parent's skillSnapshot after a Create-draft round-trip
      so the diff cards immediately repaint against the draft body. */
  onSnapshotChange?: (snapshot: Record<string, unknown>) => void
  onApplied?: () => void
}

// Right-side approval pane: renders Forge's proposed changes as diff
// cards, lets the operator deselect / remove individual ones, then
// applies the accepted set against the same PATCH /skills/:id/apply_proposals
// endpoint DesignerPane uses.
//
// Differences from the DesignerPane right pane:
//   - No persisted DesignerChatLog round-trip — proposals live in
//     in-memory state only (modal is ephemeral).
//   - No "regenerate" button: the modal is a triage tool, not a
//     workshop. If the operator wants a redo, they ask Forge in chat.
//   - "Open in designer" cross-link still available from the chat
//     pane for cases that need the full Designer flow.
export function ForgeProposalReview({
  skillId, proposals, setProposals, skillSnapshot, scenarios, isDraft, onSnapshotChange, onApplied,
}: Props) {
  const [applying, setApplying]         = useState(false)
  const [applyError, setApplyError]     = useState<string | null>(null)
  const [creatingDraft, setCreatingDraft] = useState(false)
  // Captured when ensure_draft returns. Used for the post-apply toast
  // ("Draft v3 ready — promote on the skill page").
  const [pendingDraftVersion, setPendingDraftVersion] = useState<number | null>(null)

  const acceptedCount = proposals.filter((p) => p.accepted).length

  function toggleProposal(id: string) {
    setProposals((cur) => cur.map((p) => (p.id === id ? { ...p, accepted: !p.accepted } : p)))
  }

  function removeProposal(id: string) {
    setProposals((cur) => cur.filter((p) => p.id !== id))
  }

  function clearAll() {
    if (proposals.length === 0) return
    if (!confirm(`Discard ${proposals.length} pending change${proposals.length === 1 ? "" : "s"}?`)) return
    setProposals([])
  }

  // Forks a draft from the live (or highest) version when the working
  // version is live. Idempotent — server returns the existing draft if
  // there already is one. `silent` swallows the success toast for the
  // auto-fork path so the operator isn't pinged about machinery.
  async function createDraft({ silent = false }: { silent?: boolean } = {}): Promise<boolean> {
    setCreatingDraft(true)
    try {
      const csrf = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      const res = await fetch(`/skills/${skillId}/ensure_draft`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": csrf,
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
      const data = await res.json().catch(() => ({})) as {
        error?: string
        draft_version_number?: number
        skillSnapshot?: Record<string, unknown>
      }
      if (!res.ok) {
        toast.error(data.error || `Couldn't create draft (HTTP ${res.status})`)
        return false
      }
      if (data.skillSnapshot) onSnapshotChange?.(data.skillSnapshot)
      if (typeof data.draft_version_number === "number") setPendingDraftVersion(data.draft_version_number)
      if (!silent) toast.success(`Draft v${data.draft_version_number} ready — review and apply.`)
      return true
    } catch (e) {
      toast.error(`Network error: ${e instanceof Error ? e.message : "unknown"}`)
      return false
    } finally {
      setCreatingDraft(false)
    }
  }

  // Auto-fork: silently fork a draft the moment Forge has surfaced an
  // accepted SKILL-TOUCHING proposal AND the working version is live.
  // Scenario-only proposals don't require a draft (scenarios aren't
  // versioned), so we deliberately skip the fork in that case — the
  // operator might apply a few assertion tweaks and never need to
  // promote anything. Guarded by a ref so subsequent proposals don't
  // re-fire the fetch.
  const autoDraftFiredRef = useRef(false)
  useEffect(() => {
    if (autoDraftFiredRef.current) return
    if (proposals.length === 0) return
    if (skillSnapshot == null) return
    if (isDraft) return
    if (!needsDraftFor(proposals)) return
    autoDraftFiredRef.current = true
    void createDraft({ silent: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals.length, isDraft, skillSnapshot])

  async function applySelected() {
    const accepted = proposals.filter((p) => p.accepted)
    if (accepted.length === 0) return
    const requiresDraft = needsDraftFor(accepted)
    // Belt-and-suspenders: if a skill-touching proposal needs a draft
    // and the auto-fork hasn't landed yet (race with a fast "click Apply
    // immediately") or the operator cleared the draft elsewhere, fork
    // now and wait. Scenario-only batches skip this entirely.
    if (requiresDraft && !isDraft) {
      const ok = await createDraft({ silent: true })
      if (!ok) return
    }
    setApplying(true)
    setApplyError(null)
    try {
      const csrf = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      const res = await fetch(`/skills/${skillId}/apply_proposals`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": csrf,
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: new URLSearchParams({
          proposals: JSON.stringify(accepted),
          skill_version_id: String(skillSnapshot?.versionId ?? ""),
        }),
      })
      const data = await res.json().catch(() => ({})) as {
        error?: string
        rerunScope?: { mode?: "suite" | "scenarios" | "skipped"; count?: number }
      }
      if (!res.ok) {
        const msg = data.error ?? `HTTP ${res.status}`
        setApplyError(msg)
        toast.error(`Apply failed: ${msg}`)
        return
      }
      const noun = `change${accepted.length === 1 ? "" : "s"}`
      // Verification reruns kicked off server-side. Tell the operator
      // what's running so the row-level "pending → running → terminal"
      // transitions they're about to see aren't a surprise.
      const rerun = data.rerunScope
      const rerunBlurb =
        rerun?.mode === "suite" && (rerun.count ?? 0) > 0
          ? ` Re-running ${rerun.count} scenario${rerun.count === 1 ? "" : "s"} against the draft.`
          : rerun?.mode === "scenarios" && (rerun.count ?? 0) > 0
          ? ` Re-running ${rerun.count} affected scenario${rerun.count === 1 ? "" : "s"}.`
          : ""
      if (requiresDraft && pendingDraftVersion != null) {
        // The operator is in the testing context and just applied a
        // skill-touching proposal. The auto-rerun we just fired is
        // most informative on the test tab (rows transition pending →
        // running → terminal in real time), so land there. Promote
        // lives at the top of the same page; one click away.
        toast.success(`Applied ${accepted.length} ${noun}. Skill is in DRAFT v${pendingDraftVersion} — watch the reruns and promote from the test tab.${rerunBlurb}`, {
          duration: 12_000,
          action: {
            label: "Open test tab",
            onClick: () => { window.location.href = `/skills/${skillId}/test?tab=test` },
          },
        })
      } else {
        toast.success(`Applied ${accepted.length} ${noun}.${rerunBlurb}`)
      }
      const appliedIds = new Set(accepted.map((p) => p.id))
      setProposals((cur) => cur.filter((p) => !appliedIds.has(p.id)))
      onApplied?.()
    } catch (e) {
      const msg = e instanceof Error ? e.message : "network error"
      setApplyError(msg)
      toast.error(`Apply failed: ${msg}`)
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="valet-panel flex h-full flex-col overflow-hidden rounded-card">
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-app-text">
          <span className="forge-icon">
            <Sparkles size={14} />
          </span>
          <span className="font-semibold text-white">Approval</span>
          <span className="text-[11px] text-app-muted">
            {proposals.length === 0
              ? "No suggestions yet"
              : acceptedCount === proposals.length
              ? `${proposals.length} to apply`
              : `${acceptedCount} of ${proposals.length} selected`}
          </span>
        </div>
        {proposals.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            title="Discard all"
            className="inline-flex items-center gap-1 rounded-control border border-border px-2 py-1 text-[11px] text-app-muted transition hover:border-status-error hover:text-status-error"
          >
            <Trash2 size={12} /> Clear
          </button>
        )}
      </header>

<div className="thin-scrollbar flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {proposals.length === 0 ? (
          <div className="flex h-full min-h-44 items-center justify-center rounded-card border border-dashed border-border bg-white/[0.02] px-6 text-center">
            <p className="max-w-sm text-sm leading-6 text-app-muted">
              Ask Forge to suggest a test or skill change. Diffs will appear here for review before you apply them.
            </p>
          </div>
        ) : (
          proposals.map((p) => (
            <ProposalDiffCard
              key={p.id}
              proposal={p}
              onToggle={toggleProposal}
              onRemove={removeProposal}
              skillSnapshot={skillSnapshot ?? {}}
              scenarios={scenarios}
            />
          ))
        )}
        {applyError && (
          <p className="rounded-control border border-status-error/30 bg-status-error/10 px-2 py-1 text-xs text-status-error">
            {applyError}
          </p>
        )}
      </div>

      <footer className="border-t border-border p-3">
        <Button
          type="button"
          variant="red"
          size="sm"
          onClick={applySelected}
          disabled={applying || creatingDraft || acceptedCount === 0}
          className="btn-red w-full rounded-control"
        >
          {applying || creatingDraft ? "Applying…" : `Apply ${acceptedCount} change${acceptedCount === 1 ? "" : "s"}`}
        </Button>
      </footer>
    </div>
  )
}
